/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getSingleClient, {
	GetSingleClientResponse,
} from 'api/clients/getSingleClient'
import WabaManagement from 'api/clients/wabaManagement'

import {
	ClientProfile,
	Plan,
	SingleClientProfileDashboard,
	WabaManagementApiResponse,
} from 'types'

/**
 * Types for the slice of single client in the store
 */
export interface SingleClientState {
	clientProfile: ClientProfile | null
	dashboard: SingleClientProfileDashboard | null
	planHistory: Plan[] | []
	loading: boolean
	wabaManagementDetails?: WabaManagementApiResponse
}

/**
 * Initial state of the slice single client
 */
const initialState: SingleClientState = {
	loading: false,
	clientProfile: null,
	dashboard: null,
	planHistory: [],
}

/**
 * Action for getting single clients
 *
 * Tries to get single clients from the API and returns the result in the payload
 *
 * @param {string} id - id of the user for single client
 * @returns {Promise<GetSingleClientResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getSingleClientAction = createAsyncThunk<
	GetSingleClientResponse,
	string
>('singleClient/getSingleClientAction', async (id, { rejectWithValue }) => {
	try {
		const response = await getSingleClient(id)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const getWabaManagementDetailsAction = createAsyncThunk<
	WabaManagementApiResponse,
	string
>('getWabaManagementDetailsAction', async (param, { rejectWithValue }) => {
	try {
		const response = await WabaManagement.getWabaPhoneDetailsForClient(param)
		return response
	} catch (er) {
		return rejectWithValue(er)
	}
})

export const singleClientSlice = createSlice({
	name: 'singleClient',
	initialState,
	reducers: {
		clearSingleClient: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getSingleClientAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getSingleClientAction.fulfilled, (state, action) => {
			state.clientProfile = action.payload.clientProfile
			state.dashboard = action.payload.dashboard
			state.planHistory = action.payload.clientProfile?.planHistory
			state.loading = false
		})
		builder.addCase(getSingleClientAction.rejected, state => {
			state.loading = false
		})
		builder.addCase(getWabaManagementDetailsAction.pending, s => {
			s.loading = true
		})
		builder.addCase(getWabaManagementDetailsAction.rejected, s => {
			s.loading = false
			s.wabaManagementDetails = undefined
		})
		builder.addCase(
			getWabaManagementDetailsAction.fulfilled,
			(s, { payload }) => {
				s.loading = false
				s.wabaManagementDetails = payload
			}
		)
	},
})

export const { clearSingleClient } = singleClientSlice.actions

export default singleClientSlice.reducer
